import React, { useState, useEffect, useMemo } from 'react';

const Icon = ({ d, size = 16, color = 'currentColor', strokeWidth = 2 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round">
        {Array.isArray(d) ? d.map((path, i) => <path key={i} d={path} />) : <path d={d} />}
    </svg>
);

const icons = {
    calendar: 'M19 4H5a2 2 0 00-2 2v14a2 2 0 00 2 2h14a2 2 0 00 2-2V6a2 2 0 00-2-2z M16 2v4 M8 2v4 M3 10h18',
    chevronLeft: 'M15 18l-6-6 6-6',
    chevronRight: 'M9 18l6-6-6-6',
    check: 'M20 6L9 17l-5-5',
    x: 'M18 6L6 18M6 6l12 12',
    user: 'M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2 M12 3a4 4 0 1 0 0 8 4 4 0 0 0 0-8z',
    clock: 'M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z',
    info: 'M12 8v4M12 16h.01M22 12A10 10 0 1 1 2 12a10 10 0 0 1 20 0z',
    plus: 'M12 5v14M5 12h14',
    save: 'M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z M17 21v-8H7v8 M7 3v5h8',
};

export default function DriverAttendance({ user, operatorDetails }) {
    const [currentDate, setCurrentDate] = useState(new Date());
    const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
    const [drivers, setDrivers] = useState([]);
    const [attendance, setAttendance] = useState([]);
    const [loading, setLoading] = useState(true);
    const [markingMode, setMarkingMode] = useState(false);
    const [toast, setToast] = useState(null);

    const showToast = (msg, type = 'success') => {
        setToast({ msg, type });
        setTimeout(() => setToast(null), 3000);
    };

    const fetchDrivers = async () => {
        try {
            const res = await fetch(`/api/drivers/${user.operatorId}`);
            const data = await res.json();
            setDrivers(Array.isArray(data) ? data : []);
        } catch (err) {
            console.error("Error fetching drivers:", err);
        }
    };

    const fetchAttendance = async () => {
        const monthStr = currentDate.toISOString().substring(0, 7); // YYYY-MM
        try {
            const res = await fetch(`/api/attendance/${user.operatorId}?month=${monthStr}`);
            const data = await res.json();
            setAttendance(Array.isArray(data) ? data : []);
            setLoading(false);
        } catch (err) {
            console.error("Error fetching attendance:", err);
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchDrivers();
    }, [user.operatorId]);

    useEffect(() => {
        fetchAttendance();
    }, [user.operatorId, currentDate]);

    const daysInMonth = useMemo(() => {
        const year = currentDate.getFullYear();
        const month = currentDate.getMonth();
        const date = new Date(year, month, 1);
        const days = [];
        // Pad with previous month's days
        const firstDayIndex = date.getDay();
        for (let i = 0; i < firstDayIndex; i++) {
            days.push(null);
        }
        while (date.getMonth() === month) {
            days.push(new Date(date));
            date.setDate(date.getDate() + 1);
        }
        return days;
    }, [currentDate]);

    const stats = useMemo(() => {
        const todayAttendance = attendance.filter(a => a.date === selectedDate);
        const presentCount = todayAttendance.filter(a => a.status === 'Present').length;
        const absentCount = todayAttendance.filter(a => a.status === 'Absent').length;
        const leaveCount = todayAttendance.filter(a => a.status === 'On Leave' || a.status === 'Half Day').length;
        const notMarkedCount = drivers.length - todayAttendance.length;

        return {
            total: drivers.length,
            present: presentCount + notMarkedCount,
            absent: absentCount,
            onLeave: leaveCount,
            notMarked: 0
        };
    }, [attendance, drivers, selectedDate]);

    const handleMonthChange = (offset) => {
        const newDate = new Date(currentDate);
        newDate.setMonth(newDate.getMonth() + offset);
        setCurrentDate(newDate);
    };

    const markAttendance = async (driverId, status) => {
        const current = attendance.find(a => a.date === selectedDate && (a.driverId?._id === driverId || a.driverId === driverId));
        const newStatus = (current && current.status === status) ? 'Present' : status;

        try {
            const res = await fetch('/api/attendance', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    operatorId: user.operatorId,
                    driverId,
                    date: selectedDate,
                    status: newStatus
                })
            });
            if (res.ok) {
                fetchAttendance();
                showToast(`Status updated to ${newStatus}`);
            }
        } catch (err) {
            showToast('Failed to update status', 'error');
        }
    };

    const bulkMarkAttendance = async (status) => {
        const todayAttendance = attendance.filter(a => a.date === selectedDate);
        const markedDriverIds = new Set(todayAttendance.map(a => a.driverId?._id || a.driverId));
        const driversToMark = drivers.filter(d => !markedDriverIds.has(d._id));
        
        if (driversToMark.length === 0) {
            showToast('All drivers already marked for this day', 'info');
            return;
        }

        try {
            const res = await fetch('/api/attendance/bulk', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    operatorId: user.operatorId,
                    date: selectedDate,
                    attendanceData: driversToMark.map(d => ({ driverId: d._id, status }))
                })
            });
            if (res.ok) {
                fetchAttendance();
                showToast(`Bulk marked as ${status}`);
            }
        } catch (err) {
            showToast('Bulk update failed', 'error');
        }
    };

    const getAttendanceForDate = (date) => {
        if (!date) return null;
        const dateStr = date.toISOString().split('T')[0];
        return attendance.filter(a => a.date === dateStr);
    };

    const getSummaryForDate = (date) => {
        const items = getAttendanceForDate(date);
        if (!items || items.length === 0) return null;
        const present = items.filter(i => i.status === 'Present').length;
        const absent = items.filter(i => i.status === 'Absent').length;
        return { present, absent };
    };

    return (
        <div className="attendance-page">
            <style>{`
                .attendance-page { padding: 30px; background: #f8fafc; min-height: 100vh; font-family: 'Inter', sans-serif; }
                .theme-dark .attendance-page { background: #0f172a; }

                .header { display: flex; justify-content: space-between; align-items: flex-end; margin-bottom: 30px; }
                .title-wrap h1 { font-size: 28px; font-weight: 800; color: #1e293b; margin: 0; }
                .title-wrap p { color: #64748b; margin: 4px 0 0; font-size: 15px; }
                .theme-dark .title-wrap h1 { color: #f1f5f9; }

                .stats-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 20px; margin-bottom: 30px; }
                .stat-card {
                    background: white; border-radius: 20px; padding: 20px; border: 1px solid #e2e8f0;
                    display: flex; align-items: center; gap: 16px; transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
                    box-shadow: 0 4px 6px -1px rgba(0,0,0,0.03);
                }
                .theme-dark .stat-card { background: #1e293b; border-color: #334155; }
                .stat-card:hover { transform: translateY(-4px); box-shadow: 0 12px 20px -5px rgba(0,0,0,0.1); }
                .stat-icon {
                    width: 52px; height: 52px; border-radius: 14px; display: flex; align-items: center; justify-content: center;
                }
                .stat-info { display: flex; flex-direction: column; }
                .stat-label { font-size: 12px; font-weight: 700; color: #64748b; text-transform: uppercase; letter-spacing: 0.05em; margin-bottom: 4px; }
                .stat-val { font-size: 26px; font-weight: 800; color: #1e293b; line-height: 1; }
                .theme-dark .stat-val { color: #f1f5f9; }

                .main-layout { display: grid; grid-template-columns: 1.2fr 0.8fr; gap: 30px; align-items: start; }
                
                /* Calendar Styling */
                .calendar-container { 
                    background: white; border-radius: 24px; padding: 24px; border: 1px solid #e2e8f0;
                    box-shadow: 0 10px 25px -5px rgba(0,0,0,0.05);
                }
                .theme-dark .calendar-container { background: #1e293b; border-color: #334155; }
                .cal-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 24px; }
                .cal-month-title { font-size: 20px; font-weight: 800; color: #1e293b; display: flex; align-items: center; gap: 10px; }
                .theme-dark .cal-month-title { color: #f1f5f9; }
                .cal-nav { display: flex; gap: 8px; }
                .cal-nav-btn {
                    width: 36px; height: 36px; border-radius: 10px; border: 1px solid #e2e8f0;
                    background: white; color: #64748b; display: flex; align-items: center; justify-content: center;
                    cursor: pointer; transition: all 0.2s;
                }
                .cal-nav-btn:hover { border-color: #6366f1; color: #6366f1; background: #f5f3ff; }
                .theme-dark .cal-nav-btn { background: #0f172a; border-color: #334155; }

                .cal-grid { display: grid; grid-template-columns: repeat(7, 1fr); gap: 10px; }
                .cal-weekday { 
                    text-align: center; font-size: 11px; font-weight: 700; color: #64748b; 
                    text-transform: uppercase; padding: 10px 0; letter-spacing: 0.1em;
                }
                .cal-day {
                    aspect-ratio: 1; border-radius: 14px; border: 1px solid #e2e8f0;
                    display: flex; flex-direction: column; align-items: center; justify-content: center;
                    cursor: pointer; position: relative; transition: all 0.2s; background: #fff;
                }
                .theme-dark .cal-day { background: #1a2233; border-color: #334155; }
                .cal-day:hover { transform: scale(1.05); border-color: #6366f1; z-index: 10; box-shadow: 0 10px 15px -3px rgba(99, 102, 241, 0.1); }
                .cal-day.empty { cursor: default; border: none; background: transparent; }
                .cal-day.today { border: 2px solid #6366f1; background: #f5f3ff; }
                .theme-dark .cal-day.today { background: #1e1b4b; border-color: #818cf8; }
                .cal-day.selected { background: #6366f1 !important; border-color: #6366f1; }
                .cal-day.selected .cal-day-num { color: white !important; }
                .cal-day-num { font-size: 16px; font-weight: 700; color: #1e293b; }
                .theme-dark .cal-day-num { color: #f1f5f9; }
                .cal-day-dots { display: flex; gap: 3px; margin-top: 4px; }
                .dot { width: 4px; height: 4px; border-radius: 50%; }
                .dot.present { background: #10b981; }
                .dot.absent { background: #ef4444; }

                /* Details List */
                .details-container {
                    background: white; border-radius: 24px; border: 1px solid #e2e8f0; overflow: hidden;
                    box-shadow: 0 10px 25px -5px rgba(0,0,0,0.05);
                }
                .theme-dark .details-container { background: #1e293b; border-color: #334155; }
                .details-header { padding: 24px; border-bottom: 1px solid #f1f5f9; display: flex; justify-content: space-between; align-items: center; }
                .theme-dark .details-header { border-bottom-color: #334155; }
                .details-title { font-size: 18px; font-weight: 700; color: #1e293b; }
                .theme-dark .details-title { color: #f1f5f9; }
                
                .action-btns { display: flex; gap: 10px; margin-bottom: 20px; padding: 0 24px; }
                .bulk-btn {
                    flex: 1; padding: 10px; border-radius: 12px; font-size: 13px; font-weight: 600;
                    display: flex; align-items: center; justify-content: center; gap: 6px; cursor: pointer;
                    transition: all 0.2s; border: 1px solid transparent;
                }
                .bulk-present { background: #ecfdf5; color: #059669; border-color: #10b981; }
                .bulk-present:hover { background: #10b981; color: white; }
                .bulk-absent { background: #fef2f2; color: #dc2626; border-color: #ef4444; }
                .bulk-absent:hover { background: #ef4444; color: white; }

                .driver-list { max-height: 500px; overflow-y: auto; padding: 0 24px 24px; }
                .driver-row {
                    display: flex; align-items: center; justify-content: space-between;
                    padding: 14px 0; border-bottom: 1px solid #f1f5f9;
                }
                .theme-dark .driver-row { border-bottom-color: #334155; }
                .driver-row:last-child { border-bottom: none; }
                .driver-info-sm { display: flex; align-items: center; gap: 12px; }
                .avatar-sm { 
                    width: 32px; height: 32px; background: #e0e7ff; color: #4338ca; border-radius: 10px;
                    display: flex; align-items: center; justify-content: center; font-weight: 700; font-size: 13px;
                }
                .driver-name-sm { font-weight: 600; color: #1e293b; font-size: 14px; }
                .theme-dark .driver-name-sm { color: #f1f5f9; }
                .driver-code-sm { font-size: 11px; color: #94a3b8; text-transform: uppercase; }

                .status-selector { display: flex; gap: 6px; }
                .status-btn {
                    width: 32px; height: 32px; border-radius: 8px; border: 1px solid #e2e8f0;
                    display: flex; align-items: center; justify-content: center; cursor: pointer; transition: all 0.2s;
                }
                .theme-dark .status-btn { background: #0f172a; border-color: #334155; }
                .status-btn.present { color: #10b981; }
                .status-btn.present.active { background: #10b981; color: white; border-color: #10b981; }
                .status-btn.absent { color: #ef4444; }
                .status-btn.absent.active { background: #ef4444; color: white; border-color: #ef4444; }
                .status-btn.leave { color: #f59e0b; }
                .status-btn.leave.active { background: #f59e0b; color: white; border-color: #f59e0b; }

                .toast {
                    position: fixed; top: 24px; right: 24px; padding: 14px 24px; border-radius: 12px;
                    background: #1e293b; color: white; z-index: 1000; box-shadow: 0 10px 15px -3px rgba(0,0,0,0.1);
                    animation: slideIn 0.3s ease-out; font-weight: 600;
                }
                .toast.success { background: #10b981; }
                .toast.error { background: #ef4444; }
                .toast.info { background: #6366f1; }
                @keyframes slideIn { from { transform: translateY(-20px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }
            `}</style>

            {toast && <div className={`toast ${toast.type}`}>{toast.msg}</div>}

            <div className="header">
                <div className="title-wrap">
                    <h1>Attendance Management</h1>
                    <p>Track and manage daily driver availability and presence</p>
                </div>
                <div className="header-actions">
                    {/* Add any header actions if needed */}
                </div>
            </div>

            <div className="stats-grid">
                <div className="stat-card" style={{ borderLeft: '4px solid #6366f1' }}>
                    <div className="stat-icon" style={{ background: '#eef2ff' }}>
                        <Icon d={icons.user} color="#4338ca" size={24} />
                    </div>
                    <div className="stat-info">
                        <span className="stat-label">Total Drivers</span>
                        <span className="stat-val">{stats.total}</span>
                    </div>
                </div>
                <div className="stat-card" style={{ borderLeft: '4px solid #10b981' }}>
                    <div className="stat-icon" style={{ background: '#ecfdf5' }}>
                        <Icon d={icons.check} color="#059669" size={24} />
                    </div>
                    <div className="stat-info">
                        <span className="stat-label">Present (Selected)</span>
                        <span className="stat-val">{stats.present}</span>
                    </div>
                </div>
                <div className="stat-card" style={{ borderLeft: '4px solid #ef4444' }}>
                    <div className="stat-icon" style={{ background: '#fef2f2' }}>
                        <Icon d={icons.x} color="#dc2626" size={24} />
                    </div>
                    <div className="stat-info">
                        <span className="stat-label">Absent (Selected)</span>
                        <span className="stat-val">{stats.absent}</span>
                    </div>
                </div>
                <div className="stat-card" style={{ borderLeft: '4px solid #f59e0b' }}>
                    <div className="stat-icon" style={{ background: '#fffbeb' }}>
                        <Icon d={icons.clock} color="#d97706" size={24} />
                    </div>
                    <div className="stat-info">
                        <span className="stat-label">Pending</span>
                        <span className="stat-val">{stats.notMarked}</span>
                    </div>
                </div>
            </div>

            <div className="main-layout">
                <div className="calendar-container">
                    <div className="cal-header">
                        <div className="cal-month-title">
                            <Icon d={icons.calendar} size={22} color="#6366f1" />
                            {currentDate.toLocaleString('default', { month: 'long', year: 'numeric' })}
                        </div>
                        <div className="cal-nav">
                            <button className="cal-nav-btn" onClick={() => handleMonthChange(-1)}><Icon d={icons.chevronLeft} size={18} /></button>
                            <button className="cal-nav-btn" onClick={() => setCurrentDate(new Date())}>Today</button>
                            <button className="cal-nav-btn" onClick={() => handleMonthChange(1)}><Icon d={icons.chevronRight} size={18} /></button>
                        </div>
                    </div>
                    <div className="cal-grid">
                        {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(d => (
                            <div key={d} className="cal-weekday">{d}</div>
                        ))}
                        {daysInMonth.map((date, i) => {
                            if (!date) return <div key={`empty-${i}`} className="cal-day empty" />;
                            const dateStr = date.toISOString().split('T')[0];
                            const isToday = dateStr === new Date().toISOString().split('T')[0];
                            const isSelected = dateStr === selectedDate;
                            const summary = getSummaryForDate(date);

                            return (
                                <div 
                                    key={dateStr} 
                                    className={`cal-day ${isToday ? 'today' : ''} ${isSelected ? 'selected' : ''}`}
                                    onClick={() => setSelectedDate(dateStr)}
                                >
                                    <span className="cal-day-num">{date.getDate()}</span>
                                    {summary && (
                                        <div className="cal-day-dots">
                                            {Array.from({ length: summary.present }).slice(0, 3).map((_, idx) => (
                                                <div key={`p-${idx}`} className="dot present" />
                                            ))}
                                            {Array.from({ length: summary.absent }).slice(0, 3).map((_, idx) => (
                                                <div key={`a-${idx}`} className="dot absent" />
                                            ))}
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>

                <div className="details-container">
                    <div className="details-header">
                        <div className="details-title">
                            {new Date(selectedDate).toLocaleDateString('default', { day: 'numeric', month: 'long', year: 'numeric' })}
                        </div>
                        <div style={{ color: '#94a3b8', fontSize: '13px', fontWeight: 600 }}>
                            {drivers.length} Total Drivers
                        </div>
                    </div>

                    <div className="driver-list">
                        <div style={{ fontSize: '12px', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', marginBottom: '12px', marginTop: '10px' }}>Driver List</div>
                        {drivers.map(driver => {
                            const att = attendance.find(a => a.date === selectedDate && (a.driverId?._id === driver._id || a.driverId === driver._id));
                            const status = att ? att.status : 'Present';
                            return (
                                <div key={driver._id} className="driver-row">
                                    <div className="driver-info-sm">
                                        <div className="avatar-sm">{driver.name?.charAt(0)}</div>
                                        <div>
                                            <div className="driver-name-sm">{driver.name}</div>
                                            <div className="driver-code-sm">{driver.driverCode || 'No Code'}</div>
                                        </div>
                                    </div>
                                    <div className="status-selector">
                                        <button 
                                            className={`status-btn absent ${status === 'Absent' ? 'active' : ''}`}
                                            title="Mark Absent"
                                            onClick={() => markAttendance(driver._id, 'Absent')}
                                        >
                                            <Icon d={icons.x} size={16} />
                                        </button>
                                        <button 
                                            className={`status-btn leave ${status === 'On Leave' ? 'active' : ''}`}
                                            title="Mark On Leave"
                                            onClick={() => markAttendance(driver._id, 'On Leave')}
                                        >
                                            <Icon d={icons.clock} size={16} />
                                        </button>
                                    </div>
                                </div>
                            );
                        })}
                        {drivers.length === 0 && (
                            <div style={{ textAlign: 'center', padding: '40px', color: '#94a3b8' }}>
                                <Icon d={icons.info} size={24} />
                                <p style={{ marginTop: '10px' }}>No drivers found</p>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
